import React, { useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { ButtonBase, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { STUDIO_THEME_COLORS } from '../../../constants';
import { normalizeHexColor } from '../../../utils/color';
import StudioColorPicker from './StudioColorPicker';

interface BackgroundControlProps {
  intl: any;
  themeColor: string;
  onChangeThemeColor: (color: string) => void;
}

const COLOR_PICKER_FALLBACK = '#f59e0b';

const BackgroundControl: React.FC<BackgroundControlProps> = ({ intl, themeColor, onChangeThemeColor }) => {
  const customAnchorRef = useRef<HTMLDivElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const normalizedTheme = normalizeHexColor(themeColor) || themeColor.toLowerCase();

  // Remember the last custom pick so it stays as a preview swatch even after the
  // user switches to a preset colour, letting them select it back with one click.
  // This only activates when the user actively picks from the custom picker, so on
  // entry the rainbow swatch is shown (custom is not auto-selected).
  const [lastCustomColor, setLastCustomColor] = useState(COLOR_PICKER_FALLBACK);
  const [hasCustomColor, setHasCustomColor] = useState(false);

  const isCustomSelected = hasCustomColor && normalizedTheme === lastCustomColor;

  const applyPresetColor = (color: string) => {
    const next = normalizeHexColor(color);
    if (!next) return;
    setPickerOpen(false);
    onChangeThemeColor(next);
  };

  const applyCustomColor = (color: string) => {
    const next = normalizeHexColor(color);
    if (!next) return;
    setLastCustomColor(next);
    setHasCustomColor(true);
    onChangeThemeColor(next);
  };

  const handleCustomSwatchClick = () => {
    // Re-select the previously chosen custom colour, then open the picker to tweak it.
    if (hasCustomColor && !isCustomSelected) {
      applyCustomColor(lastCustomColor);
    }
    setPickerOpen(true);
  };

  return (
    <div className="studio-control-content">
      <StudioColorPicker
        open={pickerOpen}
        anchorEl={customAnchorRef.current}
        color={lastCustomColor}
        title={intl.formatMessage({ id: 'hot-topic-studio-custom-color' })}
        onClose={() => setPickerOpen(false)}
        onChange={applyCustomColor}
      />
      <Typography variant="subtitle2" className="studio-control-label">
        {intl.formatMessage({ id: 'hot-topic-studio-theme-color' })}
      </Typography>
      <div className="studio-color-swatches">
        {STUDIO_THEME_COLORS.map(color => {
          const selected = !isCustomSelected && normalizedTheme === color.toLowerCase();
          return (
            <ButtonBase
              key={color}
              aria-label={color}
              className={`studio-color-swatch${selected ? ' selected' : ''}`}
              style={{ background: color }}
              onClick={() => applyPresetColor(color)}
            >
              {selected && <CheckIcon />}
            </ButtonBase>
          );
        })}

        <div className="studio-color-swatch-custom-wrap" ref={customAnchorRef}>
          <ButtonBase
            aria-label={intl.formatMessage({ id: 'hot-topic-studio-custom-color' })}
            className={`studio-color-swatch studio-color-swatch-custom${hasCustomColor ? ' has-color' : ''}${
              isCustomSelected ? ' selected' : ''
            }`}
            style={hasCustomColor ? { background: lastCustomColor } : undefined}
            onClick={handleCustomSwatchClick}
          >
            {isCustomSelected ? (
              <CheckIcon />
            ) : hasCustomColor ? null : (
              <span className="studio-color-swatch-plus">+</span>
            )}
            {hasCustomColor && (
              <span className="studio-color-swatch-edit">
                <EditIcon />
              </span>
            )}
          </ButtonBase>
        </div>
      </div>
    </div>
  );
};

export default injectIntl(BackgroundControl);
